// Px3 Pipeline - sequencer profiles, read-only.
//
// A profile is the server-side sending/warmup baseline an order names in
// `sequencer.profile`; the payload may override individual fields but cannot
// invent one. There is no editor here on purpose: a profile change silently
// re-aims every order that names it, so it belongs in a migration with a diff
// and a review, not behind a text box on a dashboard.
//
// The settings are printed as raw JSON rather than a field table because the
// shape is the sequencer's, not ours - the whole value of this screen is that
// what an operator reads matches what the portal engineer sends field for field.

import { useCallback, useEffect, useState } from 'react';

import { px3Api, px3ErrorMessage, type Px3Profile } from '../px3Api';

export function ProfilesView(): JSX.Element {
  const [profiles, setProfiles] = useState<Px3Profile[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      setProfiles(await px3Api.listProfiles());
      setError(null);
    } catch (err) {
      setError(px3ErrorMessage(err));
    } finally {
      setLoaded(true);
    }
  }, []);

  // No polling: nothing in the app writes these rows.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <>
      {error && <div className="px3-toast px3-toast--error">Could not load profiles: {error}</div>}

      <section className="px3-card">
        <div className="px3-card-header">
          <h2 className="px3-card-title">Sequencer profiles</h2>
          <div className="px3-actions">
            <button
              type="button"
              className="px3-btn px3-btn--ghost px3-btn--small"
              onClick={() => void refresh()}
            >
              Refresh
            </button>
          </div>
        </div>
        <div className="px3-card-body">
          <span className="px3-hint">
            An order names one of these in <code>sequencer.profile</code>. Anything in its
            <code> sequencer.settings</code> overrides the matching field for that order only;
            unknown keys are rejected at intake.
          </span>
          {profiles.length === 0 ? (
            <div className="px3-empty">
              {!loaded
                ? 'Loading profiles...'
                : error !== null
                  ? 'The profile list could not be loaded - this is not an empty list.'
                  : 'No profiles. Every order will be rejected at intake until one exists.'}
            </div>
          ) : (
            profiles.map((profile) => (
              <div key={profile.name} className="px3-field">
                <span className="px3-field-label">
                  {profile.name} - {profile.provider}
                </span>
                <span className="px3-hint">
                  tags: {profile.tags.length > 0 ? profile.tags.join(', ') : 'none'} - per-mailbox
                  group tags: {profile.group_tags ? 'yes' : 'no'} - tracking domain:{' '}
                  {profile.tracking ? 'yes' : 'no'}
                </span>
                <pre className="px3-pre">{JSON.stringify(profile.settings, null, 2)}</pre>
              </div>
            ))
          )}
        </div>
      </section>
    </>
  );
}
